import { useState, useMemo, useEffect, createElement } from "react";
import { useNavigate } from "react-router-dom";
import theme from "../../config/theme";
import { RichMenuDataDto } from "../../dtos/richMenu.dto";
import Layout from "../../layout/layout";
import Pagination from "../../common/pagination";
import Table from "../../common/table";
import RichMenuTabs from "../../components/richMenu/richMenuTabs";
import DeleteDialog from "./delete.dialog";
import ActionMenu from "../../common/actionMenu";
import SnackBar from "../../common/snackBar";
import { paginate } from "../../utils/pagination";
import { GoDotFill } from "react-icons/go";
import { IoAddOutline } from "react-icons/io5";
import { richMenuApi } from "../../utils/apiClient";
import { getErrorMessage } from "../../utils/errorMessages";

const RichMenuPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<
    "all" | "active" | "schedule" | "archive"
  >("all");
  const [richMenuData, setRichMenuData] = useState<RichMenuDataDto[]>([]);
  const [counts, setCounts] = useState({
    total: 0,
    totalActive: 0,
    totalSchedule: 0,
    totalArchived: 0,
  });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [showDeleteSuccessSnackbar, setShowDeleteSuccessSnackbar] =
    useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Fetch counts on mount and when needed
  const fetchCounts = async () => {
    try {
      const countsData = await richMenuApi.getCounts();
      setCounts(countsData);
    } catch (error) {
      console.error("Failed to fetch rich menu counts:", error);
    }
  };

  // Fetch rich menus based on active tab
  const fetchRichMenus = async (
    filter?: "active" | "schedule" | "archived"
  ) => {
    setLoading(true);
    try {
      const response = await richMenuApi.getRichMenus(filter);
      // Extract data array from response object { data: [...], meta: {...} }
      const menuArray =
        response?.data && Array.isArray(response.data) ? response.data : [];
      // Ensure id is string for consistency
      const normalizedData = menuArray.map((item) => ({
        ...item,
        id: String(item.id),
      }));
      setRichMenuData(normalizedData);
    } catch (error) {
      console.error("Failed to fetch rich menus:", error);
      setRichMenuData([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchCounts();
    fetchRichMenus();
  }, []);

  // Fetch data when tab changes
  useEffect(() => {
    const filterMap: Record<
      "all" | "active" | "schedule" | "archive",
      "active" | "schedule" | "archived" | undefined
    > = {
      all: undefined,
      active: "active",
      schedule: "schedule",
      archive: "archived",
    };
    fetchRichMenus(filterMap[activeTab]);
    // Refresh counts when tab changes
    fetchCounts();
  }, [activeTab]);

  // Use richMenuData directly (no client-side filtering needed)
  // Ensure filteredData is always an array
  const filteredData = Array.isArray(richMenuData) ? richMenuData : [];

  // Pagination
  const { paginatedData, totalItems, totalPages } = paginate(
    filteredData,
    currentPage,
    itemsPerPage
  );

  // Reset to first page when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Toggle active status
  const toggleActive = async (id: string | number) => {
    const idStr = String(id);
    try {
      // Optimistically update UI
      setRichMenuData((prev) =>
        prev.map((item) =>
          String(item.id) === idStr
            ? { ...item, active: !(item.active || item.isActive) }
            : item
        )
      );

      // Call API to toggle active status
      const updatedData = await richMenuApi.toggleActive(id);
      console.log(updatedData);
      // Update with server response - merge all fields to ensure consistency
      setRichMenuData((prev) =>
        prev.map((item) =>
          String(item.id) === idStr
            ? { ...item, ...updatedData, id: String(updatedData.id) }
            : item
        )
      );

      // Refresh counts and refetch list to ensure item appears in correct tab
      fetchCounts();
      const filterMap: Record<
        "all" | "active" | "schedule" | "archive",
        "active" | "schedule" | "archived" | undefined
      > = {
        all: undefined,
        active: "active",
        schedule: "schedule",
        archive: "archived",
      };
      fetchRichMenus(filterMap[activeTab]);
    } catch (error) {
      console.log("Failed to toggle active status:", error);
      // Revert optimistic update on error
      setRichMenuData((prev) =>
        prev.map((item) =>
          String(item.id) === idStr
            ? { ...item, active: !(item.active || item.isActive) }
            : item
        )
      );
      // Show error message to user

      console.log(error);
      setErrorMessage(getErrorMessage(error));
      setShowErrorSnackbar(true);
    }
  };

  // Handle edit
  const handleEdit = (id: string | number) => {
    navigate(`/rich-menu/${id}`);
  };

  // Handle delete - open dialog
  const handleDelete = (id: string | number) => {
    setItemToDelete(String(id));
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      await richMenuApi.deleteRichMenu(itemToDelete);
      setItemToDelete(null);
      setDeleteDialogOpen(false);
      setShowDeleteSuccessSnackbar(true);
      // Refresh counts and list after delete
      fetchCounts();
      // Refetch the current filter to refresh the list
      const filterMap: Record<
        "all" | "active" | "schedule" | "archive",
        "active" | "schedule" | "archived" | undefined
      > = {
        all: undefined,
        active: "active",
        schedule: "schedule",
        archive: "archived",
      };
      fetchRichMenus(filterMap[activeTab]);
    } catch (error) {
      console.error("Failed to delete rich menu:", error);
      // Show error message to user
      setErrorMessage(getErrorMessage(error));
      setShowErrorSnackbar(true);
    }
  };

  // Cancel delete
  const handleCancelDelete = () => {
    setItemToDelete(null);
    setDeleteDialogOpen(false);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Format date from YYYY-MM-DD to dd-mm-yyyy
  const formatDateToDDMMYYYY = (dateString: string | undefined): string => {
    if (!dateString) return "";
    try {
      // Handle YYYY-MM-DD format
      const parts = dateString.split("-");
      if (parts.length === 3) {
        const [year, month, day] = parts;
        return `${day}/${month}/${year}`;
      }
      // If already in different format, return as is
      return dateString;
    } catch {
      return dateString;
    }
  };

  //! Render
  const header = ["Image", "Name", "Segment name", "Date", "Active"];
  const renderRow = (item: RichMenuDataDto) => {
    return (
      <>
        {/* Image */}
        <td className="px-6 py-4 whitespace-nowrap">
          <img
            src={item.imageUrl || item.image || "https://placehold.co/240x72"}
            alt={item.name}
            className="w-[240px] rounded-sm object-cover"
          />
        </td>
        {/* Name */}
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex flex-col items-start space-y-2">
            <span
              className="text-sm font-medium"
              style={{ color: theme.color.gray500 }}
            >
              {item.name}
            </span>
            {(() => {
              const isActive = Boolean(item.active || item.isActive);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              // Parse date string as local date to avoid UTC offset issues
              const parseLocalDate = (dateStr: string) => {
                if (!dateStr) return null;
                const parts = dateStr.split("-");
                if (parts.length === 3) {
                  const [year, month, day] = parts.map(Number);
                  return new Date(year, month - 1, day);
                }
                return new Date(dateStr);
              };

              const startDate = item.startDate
                ? parseLocalDate(item.startDate)
                : null;
              const endDate = item.endDate
                ? parseLocalDate(item.endDate)
                : null;

              // Determine status
              let statusLabel = "";
              let dotColor = theme.color.gray300;

              // Active always wins even if dates are future/past
              const isWithinDateRange =
                (!startDate || startDate <= today) &&
                (!endDate || endDate >= today);

              if (isActive && isWithinDateRange) {
                statusLabel = "Active";
                dotColor = theme.color.success;
              } else if (isActive && startDate && startDate > today) {
                // Mark as schedule only when flagged active and start date is in the future
                statusLabel = "Schedule";
                dotColor = "#F59E0B";
              } else {
                // Default to inactive for any other state (including expired)
                statusLabel = "Inactive";
                dotColor = theme.color.gray300;
              }

              return (
                <span
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                  style={{
                    border: `1px solid ${theme.color.light300}`,
                    color: dotColor,
                  }}
                >
                  {createElement(GoDotFill as any, {
                    className: "w-3 h-3 mr-1",
                    style: { color: dotColor },
                  })}
                  <span style={{ color: theme.color.gray500 }}>
                    {statusLabel}
                  </span>
                </span>
              );
            })()}
          </div>
        </td>
        {/* Segment Name */}
        <td
          className="px-6 py-4 whitespace-nowrap text-sm"
          style={{ color: theme.color.gray500 }}
        >
          {item.segmentName && item.segmentName.toLowerCase() !== "all"
            ? item.segmentName
            : "ทั้งหมด"}
        </td>
        <td
          className="px-6 py-4 whitespace-nowrap text-sm"
          style={{ color: theme.color.gray500 }}
        >
          {item.startDate && item.endDate
            ? `${formatDateToDDMMYYYY(item.startDate)} - ${formatDateToDDMMYYYY(
                item.endDate
              )}`
            : item.date
            ? formatDateToDDMMYYYY(item.date)
            : "N/A"}
        </td>
        {/* Active */}
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center justify-space-between gap-4">
            <button
              onClick={() => toggleActive(String(item.id))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2`}
              style={{
                backgroundColor: Boolean(item.active || item.isActive)
                  ? theme.color.greenLight
                  : theme.color.light300,
              }}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full transition-transform ${
                  Boolean(item.active || item.isActive)
                    ? "translate-x-6"
                    : "translate-x-1"
                }`}
                style={{ backgroundColor: theme.color.light100 }}
              />
            </button>

            <ActionMenu
              itemId={String(item.id)}
              onEdit={handleEdit}
              onDelete={handleDelete}
              className="mt-2"
            />
          </div>
        </td>
      </>
    );
  };

  return (
    <Layout currentPath="/rich-menu">
      {/* Delete Dialog */}
      <DeleteDialog
        isOpen={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
      {/* Success SnackBar for delete */}
      {showDeleteSuccessSnackbar && (
        <SnackBar
          message="Richmenu Deleted Successfully"
          type="success"
          position="top-right"
          show={showDeleteSuccessSnackbar}
          onClose={() => setShowDeleteSuccessSnackbar(false)}
        />
      )}
      {/* Error SnackBar */}
      {showErrorSnackbar && (
        <SnackBar
          message={errorMessage}
          type="error"
          position="top-right"
          show={showErrorSnackbar}
          onClose={() => setShowErrorSnackbar(false)}
        />
      )}
      <div className="flex flex-col overflow-hidden">
        {/* Page Header */}
        <div className="flex justify-between items-center mb-6">
          <h1
            className="text-2xl font-medium"
            style={{ color: theme.color.gray500 }}
          >
            Rich Menu
          </h1>
          <button
            onClick={() => navigate("/rich-menu/new")}
            className="flex items-center px-4 py-2 rounded-lg text-white font-medium transition-colors hover:shadow-lg"
            style={{ backgroundColor: theme.brandColor.red }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "0.9";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
          >
            {createElement(IoAddOutline as any, {
              className: "w-5 h-5 mr-2",
            })}
            Add Rich Menu
          </button>
        </div>

        {/* Tabs */}
        <RichMenuTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          counts={counts}
        />

        {/* Table Container - Flexible */}
        <div className="max-h-[calc(100vh-295px)] flex flex-col relative">
          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-sm" style={{ color: theme.color.gray300 }}>
                Loading...
              </div>
            </div>
          ) : (
            <Table
              data={paginatedData}
              className={`h-[100%] flex-1 ${totalPages > 1 ? "pb-0" : ""}`}
              header={header}
              renderRow={renderRow}
            />
          )}

          {/* Pagination - Fixed at bottom */}
          {!loading && totalPages > 0 && (
            <div className="fixed bottom-0 left-64 right-0 mx-6 py-4 z-10">
              <div className="flex justify-center">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  itemsPerPage={itemsPerPage}
                  totalItems={totalItems}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default RichMenuPage;
